import * as Sentry from '@sentry/react';
import { toast } from 'react-toastify';
import dayjs from 'dayjs';
import { getFaultName } from '@feedflo/fault-codes';
import { ALERTABLE_FAULT_CODES } from '../index';
import {
  GetFarmDataQuery,
  GetFeedEventCountQuery,
  GetFaultCountQuery,
  GetFeedFrameExportQuery,
  GetFaultCodeExportQuery,
  GetDailyConsumptionQuery,
} from './queries';

const REQUEST_LIMIT = 500;

class Factory {
  constructor({
    farmIds,
    startTime,
    stopTime,
    gqlClient,
    strategy,
    progressCallback,
    analysisFilter,
    includeStaffOnlyRootCauses,
    algorithmVersion,
    timeZone,
  }) {
    this.strategy = strategy;
    this.progressCallback = progressCallback;
    this.farmIds = farmIds;
    this.farmData = [];
    this.eventData = [];
    this.startTime = dayjs.tz(startTime).unix();
    this.stopTime = dayjs.tz(stopTime).unix();
    this.gqlClient = gqlClient;
    this.analysisFilter = analysisFilter;
    this.includeStaffOnlyRootCauses = includeStaffOnlyRootCauses;
    this.algorithmVersion = algorithmVersion;
    this.timeZone = timeZone || dayjs.tz.guess();
  }

  async start() {
    if (
      !this.strategy ||
      (!this.strategy.processFaultCodes &&
        !this.strategy.processFeedFramePage &&
        !this.strategy.processDailyConsumption)
    ) {
      Sentry?.captureMessage('DocumentFactory does not have any strategies provided');
    }

    this.progressCallback(0);

    this.farmData = await this.getFarmData(this.farmIds);

    if (this.strategy?.processFeedFramePage) {
      await this.downloadFeedFrames();
    }
    if (this.strategy?.processFaultCodes) {
      await this.downloadFaultCodes();
    }
    if (this.strategy?.processDailyConsumption) {
      await this.downloadDailyConsumption();
    }

    if (
      !this.strategy?.processFaultCodes &&
      !this.strategy?.processFeedFramePage &&
      !this.strategy?.processDailyConsumption
    ) {
      console.error('A strategy should be defined');
      const progressTimerId = setTimeout(() => {
        // Re-enable button after error disappearins
        this.progressCallback(1);
      }, 4000);
      toast.error('An error occurred. Have you selected an option for each field?', {
        position: 'top-right',
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        onClose: () => {
          clearTimeout(progressTimerId);
          this.progressCallback(1);
        },
      });
    }
  }

  safeGetFaultName(code) {
    try {
      return getFaultName(code);
    } catch (error) {
      return code;
    }
  }

  async downloadFaultCodes() {
    let downloadedDataPoints = 0;

    const totalNumberOfDataPoints = await this.getNumFaultCodes();
    this.strategy.setFarmObjects(this.farmData);

    for (const farm of this.farmData) {
      for (const line of farm.feed_lines) {
        let currEventData = null;
        let offset = 0;
        do {
          // eslint-disable-next-line no-await-in-loop
          currEventData = await this.getPageOfFaultCodesForLine(line.id, REQUEST_LIMIT, offset);
          this.strategy.processFaultCodes(farm.id, line.id, currEventData);
          offset += currEventData.length;
          downloadedDataPoints += currEventData.length;
          this.progressCallback(downloadedDataPoints / totalNumberOfDataPoints);
        } while (currEventData.length >= REQUEST_LIMIT);
      }
    }
  }

  async downloadFeedFrames() {
    let downloadedDataPoints = 0;

    const totalNumberOfDataPoints = await this.getNumFeedEvents();
    this.strategy.setFarmObjects(this.farmData);

    for (const farm of this.farmData) {
      for (const line of farm.feed_lines) {
        let currEventData = null;
        let offset = 0;
        do {
          // eslint-disable-next-line no-await-in-loop
          currEventData = await this.getPageOfFeedFrameDataForLine(line.id, REQUEST_LIMIT, offset);
          const filteredEventData = currEventData.filter((frame) => {
            return frame.mass;
          });
          this.strategy.processFeedFramePage(farm.id, line.id, filteredEventData);
          offset += currEventData.length;
          downloadedDataPoints += currEventData.length;
          this.progressCallback(downloadedDataPoints / totalNumberOfDataPoints);
        } while (currEventData.length >= REQUEST_LIMIT);
      }
    }
  }

  async getNumFeedEvents() {
    const lineReducer = (arr, farm) => arr.concat(farm.feed_lines);
    const idReducer = (arr, line) => arr.concat(line.id);
    const feedLineIDs = this.farmData.reduce(lineReducer, []).reduce(idReducer, []);

    return this.gqlClient
      .query({
        query: GetFeedEventCountQuery,
        variables: {
          feedLineIDs,
          startedAt: this.startTime,
          endedAt: this.stopTime,
        },
        fetchPolicy: 'network-only',
      })
      .then((data) => {
        return data.data.feed_frame_aggregate.aggregate.count;
      });
  }

  async getNumFaultCodes() {
    const lineReducer = (arr, farm) => arr.concat(farm.feed_lines);
    const idReducer = (arr, line) => arr.concat(line.id);
    const feedLineIDs = this.farmData.reduce(lineReducer, []).reduce(idReducer, []);

    return this.gqlClient
      .query({
        query: GetFaultCountQuery,
        variables: {
          feedLineIDs,
          startedAt: this.startTime,
          endedAt: this.stopTime,
          faultCodes: ALERTABLE_FAULT_CODES,
        },
        fetchPolicy: 'network-only',
      })
      .then((data) => {
        return data.data.fault_aggregate.aggregate.count;
      });
  }

  createFile() {
    return this.strategy?.createFile();
  }

  async getPageOfFeedFrameDataForLine(feedLineID, limit, offset) {
    return this.gqlClient
      .query({
        query: GetFeedFrameExportQuery,
        variables: {
          feedLineID,
          startedAt: this.startTime,
          endedAt: this.stopTime,
          limit,
          offset,
          feedFrameAnalysisWhere: this.analysisFilter,
        },
        fetchPolicy: 'network-only',
      })
      .then((data) => {
        return data.data.feed_frame.map((frame) => {
          return {
            s: frame.started_at,
            e: frame.ended_at,
            mass: frame.feed_frame_analyses[0]?.latest_estimated_mass_moved_in_grams,
          };
        });
      });
  }

  async getPageOfFaultCodesForLine(feedLineID, limit, offset) {
    const faultFilter = this.includeStaffOnlyRootCauses
      ? {
          user_type_can_see: {
            // Show everything that you can see at your user permission level
            _or: [{ admin_can_see: { _in: [true, false] } }, { user_can_see: { _in: [true, false] } }],
          },
        }
      : {
          user_type_can_see: {
            // Show only what admins or users can see
            _or: [{ admin_can_see: { _eq: true } }, { user_can_see: { _eq: true } }],
          },
        };

    return this.gqlClient
      .query({
        query: GetFaultCodeExportQuery,
        variables: {
          feedLineID,
          startedAt: this.startTime,
          endedAt: this.stopTime,
          faultCodes: ALERTABLE_FAULT_CODES,
          limit,
          offset,
          faultFilter,
          algorithmVersionOverride: this.algorithmVersion,
          hasAlgorithmOverride: !!this.algorithmVersion,
        },
        fetchPolicy: 'network-only',
        context: {
          headers: {
            'x-hasura-role': 'user',
          },
        },
      })
      .then((data) => {
        return data.data.fault.map((f) => {
          // Helper to get nofeed_seconds based on override status
          const getNofeedSeconds = (fault) => {
            if (this.algorithmVersion && fault.fault_nofeed_times?.length > 0) {
              return fault.fault_nofeed_times[0].nofeed_time_in_seconds;
            }
            return fault.nofeed_seconds;
          };

          const nofeedSeconds = getNofeedSeconds(f);

          return {
            s: f.started_at,
            e: f?.ended_at,
            faultText: this.safeGetFaultName(f.code),
            faultComment: f.fault_comments[0]?.comment,
            rootCause: f.fault_comments[0]?.fault_root_cause?.reference_id,
            animalGroup: f.animal_groups[0]?.external_id,
            duration: f.duration,
            nofeedSeconds: Math.max(0, nofeedSeconds || 0),
          };
        });
      });
  }

  getFarmData(farmIDs) {
    return this.gqlClient
      .query({
        query: GetFarmDataQuery,
        variables: {
          farmIDs,
        },
        fetchPolicy: 'network-only',
      })
      .then((response) => {
        return response.data.farm;
      });
  }

  async downloadDailyConsumption() {
    this.progressCallback(0.1);
    this.strategy.setFarmObjects(this.farmData);

    const dailyConsumptionData = await this.getDailyConsumptionData(this.farmIds);
    this.progressCallback(0.5);

    // Process the aggregated data
    this.strategy.processDailyConsumption(dailyConsumptionData);
    this.progressCallback(1);
  }

  async getDailyConsumptionData(barnIds) {
    const alg = this.algorithmVersion || null;

    // Fetch all consumption data in a single query
    const result = await this.gqlClient.query({
      query: GetDailyConsumptionQuery,
      variables: {
        barnIds,
        start: this.startTime,
        end: this.stopTime,
        timeZone: this.timeZone,
        alg,
      },
      fetchPolicy: 'network-only',
    });

    const farmData = result.data.farm;

    // Aggregate data by date
    const aggregatedData = {};

    // Process barn-level data
    farmData.forEach((farm) => {
      farm.per_animal_feed_summaries.forEach((summary) => {
        const dateKey = summary.start_timestamp.toString();
        if (!aggregatedData[dateKey]) {
          aggregatedData[dateKey] = {
            date: summary.start_timestamp,
            endDate: summary.end_timestamp,
            barns: [],
            rooms: [],
            feedlines: [],
          };
        }
        const barnEntry = aggregatedData[dateKey].barns.find((b) => b.id === farm.id);
        if (barnEntry) {
          barnEntry.total = summary.total_consumption;
          barnEntry.perAnimal = summary.per_animal_consumption;
        } else {
          aggregatedData[dateKey].barns.push({
            id: farm.id,
            name: farm.name,
            total: summary.total_consumption,
            perAnimal: summary.per_animal_consumption,
          });
        }
      });
    });

    // Process room-level data
    farmData.forEach((farm) => {
      farm.rooms.forEach((room) => {
        room.per_animal_feed_summaries.forEach((summary) => {
          const dateKey = summary.start_timestamp.toString();
          if (!aggregatedData[dateKey]) {
            aggregatedData[dateKey] = {
              date: summary.start_timestamp,
              endDate: summary.end_timestamp,
              barns: [],
              rooms: [],
              feedlines: [],
            };
          }
          // Check if room entry already exists for this date
          let roomEntry = aggregatedData[dateKey].rooms.find((r) => r.id === room.id && r.barnId === farm.id);
          if (!roomEntry) {
            roomEntry = {
              id: room.id,
              name: room.name,
              barnId: farm.id,
              barnName: farm.name,
              total: summary.total_consumption,
              perAnimal: summary.per_animal_consumption,
            };
            aggregatedData[dateKey].rooms.push(roomEntry);
          } else {
            roomEntry.total = summary.total_consumption;
            roomEntry.perAnimal = summary.per_animal_consumption;
          }
        });
      });
    });

    // Process feedline-level data
    farmData.forEach((farm) => {
      farm.feed_lines.forEach((feedline) => {
        feedline.feed_frame_summaries.forEach((summary) => {
          const dateKey = summary.start_timestamp.toString();
          if (!aggregatedData[dateKey]) {
            aggregatedData[dateKey] = {
              date: summary.start_timestamp,
              endDate: summary.end_timestamp,
              barns: [],
              rooms: [],
              feedlines: [],
            };
          }
          // Check if feedline entry already exists for this date
          let feedlineEntry = aggregatedData[dateKey].feedlines.find(
            (f) => f.id === feedline.id && f.barnId === farm.id,
          );
          if (!feedlineEntry) {
            feedlineEntry = {
              id: feedline.id,
              name: feedline.name,
              barnId: farm.id,
              barnName: farm.name,
              total: summary.total_consumption,
              perAnimal: null, // Feedlines don't have per-animal data
            };
            aggregatedData[dateKey].feedlines.push(feedlineEntry);
          } else {
            feedlineEntry.total = summary.total_consumption;
          }
        });
      });
    });

    // Convert to array and sort by date
    const sortedDates = Object.values(aggregatedData).sort((a, b) => a.date - b.date);

    // Collect all unique rooms and feedlines across all dates to determine column order
    const roomSet = new Set();
    const feedlineSet = new Set();
    sortedDates.forEach((dayData) => {
      dayData.rooms.forEach((room) => {
        roomSet.add(JSON.stringify({ id: room.id, name: room.name, barnId: room.barnId }));
      });
      dayData.feedlines.forEach((feedline) => {
        feedlineSet.add(JSON.stringify({ id: feedline.id, name: feedline.name, barnId: feedline.barnId }));
      });
    });

    // Convert sets to sorted arrays (alphabetically by name)
    const allRooms = Array.from(roomSet)
      .map((r) => JSON.parse(r))
      .sort((a, b) => a.name.localeCompare(b.name));
    const allFeedlines = Array.from(feedlineSet)
      .map((f) => JSON.parse(f))
      .sort((a, b) => a.name.localeCompare(b.name));

    // Reorganize data into columnar format
    const columnarData = sortedDates.map((dayData) => {
      // Get barn data (assuming single barn per export, or we take the first)
      const barn = dayData.barns[0] || { name: '', total: null, perAnimal: null };

      // Create a map for quick lookup
      const roomMap = new Map();
      dayData.rooms.forEach((room) => {
        roomMap.set(`${room.barnId}-${room.id}`, room);
      });

      const feedlineMap = new Map();
      dayData.feedlines.forEach((feedline) => {
        feedlineMap.set(`${feedline.barnId}-${feedline.id}`, feedline);
      });

      // Build row data
      const row = {
        date: dayData.date,
        endDate: dayData.endDate,
        barnName: barn.name,
        wholeBarn: barn.total,
        wholeBarnPerAnimal: barn.perAnimal,
        rooms: allRooms.map((room) => {
          const roomData = roomMap.get(`${room.barnId}-${room.id}`);
          return {
            name: room.name,
            total: roomData?.total || null,
            perAnimal: roomData?.perAnimal || null,
          };
        }),
        feedlines: allFeedlines.map((feedline) => {
          const feedlineData = feedlineMap.get(`${feedline.barnId}-${feedline.id}`);
          return {
            name: feedline.name,
            total: feedlineData?.total || null,
            perAnimal: null, // Feedlines don't have per-animal data
          };
        }),
      };

      return row;
    });

    return {
      columnarData,
      roomNames: allRooms.map((r) => r.name),
      feedlineNames: allFeedlines.map((f) => f.name),
    };
  }
}

export default Factory;
